import { Col, Row } from 'antd';
import { DefaultOptionType } from 'antd/es/select';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';

import { showBasePopup } from '@/components/base-popup/BasePopup';
import { FormSelect } from '@/components/form-select/FormSelect';

import { API } from '@/utils/constants/Apis';
import { TYPE } from '@/utils/constants/AppConstants';
import { _guuid } from '@/utils/helpers/globalHelper';

import { axiosInstance } from '@/config/http';

import { FormInputSelect } from '../../../../../components/form-input-select/FormInputSelect';
import { FormInput } from '../../../../../components/form-input/FormInput';
import ReferenceTemplateFilter from '../../../../../components/reference-template-filter/ReferenceTemplateFilter';
import useOptionsGlobal from '../../../../../hooks/useOptionsGlobal';

interface IScheduleProps {
  method?: any;
  name: 'schedules';
  getDocumentOptions: (countryId: string) => void;
  getTemplateDetail: (templateId?: string) => void;
  fetchAssignmentForm: () => void;
  optionsVisaCategory: DefaultOptionType[];
  formOptions: DefaultOptionType[];
  referenceOriginId: string;
}

function AddEditScheduleTemplate(props: IScheduleProps) {
  const { method, getTemplateDetail, formOptions, optionsVisaCategory, referenceOriginId } = props;
  const [t] = useTranslation();
  const { countryOptions } = useOptionsGlobal();
  const { id } = useParams();

  const { countryId, templateName } = method?.watch();

  const getNextCategoryOrder = async () => {
    const refOriginId = id ?? referenceOriginId;

    if (!countryId || !refOriginId) {
      return 1;
    }

    try {
      const response = await axiosInstance.get(API.GET_CATEGORY(countryId, 'TEMPLATE', refOriginId));
      const categories = Array.isArray(response?.data) ? response.data : response?.data?.data ?? [];
      const maxOrder = categories.reduce((max: number, item: any) => {
        const order = Number(item?.displayOrder ?? item?.order ?? 0);
        return Number.isFinite(order) ? Math.max(max, order) : max;
      }, 0);

      return maxOrder > 0 ? maxOrder + 1 : categories.length + 1;
    } catch (error) {
      return 1;
    }
  };

  const handleTemplateCategoryChange = (value: any) => {
    const selectedValues = Array.isArray(value) ? value : value ? [value] : [];
    const dedupedValues = Array.from(
      selectedValues
        .reduce((map: Map<string, string>, item: any) => {
          const selectedValue = typeof item === 'object' ? item?.value : item;
          const [categoryId] = String(selectedValue ?? '').split('::');

          if (categoryId) {
            map.set(String(categoryId), String(selectedValue));
          }

          return map;
        }, new Map())
        .values()
    );

    method?.setValue('templateCategories', dedupedValues);
  };

  useEffect(() => {
    if (!templateName) return;
    method.trigger('templateName');
  }, [countryId]);

  const onSelectCountry = async (values: string) => {
    if (
      method?.watch('templateCategories') ||
      method?.watch('templateId') ||
      method?.watch('patternId') ||
      method?.watch('materials')?.length ||
      method?.watch('tasks')?.length ||
      !values
    ) {
      const show = await showBasePopup({
        type: TYPE.CONFIRM,
        title: t('common:MSG_C_023.title') || '',
        msg: t('common:MSG_C_023.content') ?? ''
      });
      return show === TYPE.CONFIRM;
    } else {
      return true;
    }
  };

  return (
    <div>
      <Row gutter={16}>
        <Col span={8}>
          <FormInputSelect
            required
            allowClear={false}
            disabled={Boolean(id)}
            label={t('template_layout:country_name')}
            name='countryId'
            id='countryId'
            options={countryOptions}
            placeholder={t('placeholder:select', { field: t('template_layout:country_name') }) as string}
            isConfirmPopup={true}
            handleSelect={(values: any) => {
              return onSelectCountry(values);
            }}
          />
        </Col>
        <Col span={8}>
          <FormSelect
            options={optionsVisaCategory}
            mode='multiple'
            name='templateCategories'
            disabled={!countryId}
            label={t('basic_information:category')}
            placeholder={t('placeholder:select', { field: t('basic_information:category') }) as string}
            allowAddOnSearch
            labelInValue={false}
            handleChange={handleTemplateCategoryChange}
            addRequest={{
              mappingParams: async (inputValue: string) => {
                const nextOrder = await getNextCategoryOrder();

                return {
                  name: inputValue,
                  countryId,
                  referenceOriginId: id ?? referenceOriginId,
                  referenceOriginType: 'TEMPLATE',
                  order: nextOrder,
                  displayOrder: nextOrder
                };
              },
              method: 'POST',
              apiEndPoint: API.ADD_CATEGORY(),
              defaultParams: {}
            }}
          />
        </Col>
        <Col span={8}>
          <FormInputSelect
            name='formId'
            labelTx='template_layout:form'
            options={formOptions}
            placeholder={t('placeholder:select', { field: t('template_layout:form') }) as string}
          />
        </Col>
      </Row>
      <Row gutter={16} className='mt-3'>
        <Col span={12}>
          <FormInput
            required
            name='templateName'
            id='templateName'
            labelTx='template:template_name'
            placeholder={t('placeholder:text_box', { field: t('template:template_name') }) as string}
            defaultValue={method?.getValues('templateName')}
          />
        </Col>
        <Col span={12}>
          <ReferenceTemplateFilter
            getTemplateDetail={getTemplateDetail}
            disabled={!countryId}
            dataBasicInfo={{ countryId }}
            method={method}
            categoryOptions={optionsVisaCategory}
            formOptions={formOptions}
          />
        </Col>
      </Row>
    </div>
  );
}

export default AddEditScheduleTemplate;
